/**
 * usePlayPageQuests — the single data + handler layer for Play-page
 * onboarding quest cards.
 *
 * FULLY DATA-DRIVEN: returns one card entry per quest the backend serves for
 * `surface=PLAY_PAGE`, ordered by prerequisite depth (quests gating on other
 * quests come after the quests they gate on). Everything a card renders —
 * name, description, task labels, counters, lock state, claim state — derives
 * from the quest config + the user's QuestProgression. Authoring a new
 * PLAY_PAGE quest in admin surfaces it here with zero frontend changes.
 *
 * NO fallback/demo content: when configs aren't loaded the list is empty and
 * mount points render nothing.
 *
 * Progression docs are the ONLY source of truth for step state. The claim
 * endpoint checks the server's questProgression, so a checkmark not backed by
 * a progression doc is a lie the claim will expose. Existing-user credit is
 * handled server-side by the reconcile, not by the UI.
 */

import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { QuestConfigResponse, QuestConfigMilestoneResponse, QuestDisplay, QuestProgression, Prerequisite, PrerequisiteType, RewardConfigResponse, RewardType } from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import { useGetQuestConfigsForUser } from "@/hooks/useGetQuestConfigsForUser";
import { useGetJourneysForUser } from "@/hooks/useGetJourneysForUser";
import { useGetQuestProgressionForUser } from "@/hooks/useGetQuestProgressionForUser";
import { useClaimQuestReward } from "@/hooks/missions/useClaimQuestReward";
import { useMyStreaks } from "@/hooks/useStreaks";
import toast from "react-hot-toast";

/** One derived task row: label + state + progress counter, all from config/progression. */
export interface DerivedStep {
    label: string;
    done: boolean;
    current: boolean;
    /**
     * Not open yet — its `opensAt` has not arrived.
     *
     * A locked step is SHOWN, not hidden: on a ladder that introduces one new
     * task at a time, what is coming next is most of the reason to come back.
     * It can never be the current step, so "what do I do now" skips past it.
     */
    locked?: boolean;
    /** Epoch ms this step opens, when it is not open yet. */
    opensAt?: number;
    /** "2 / 3" style counter — set for EVERY counter milestone (incl. single-shot "0 / 1"). */
    progressText?: string;
    /** Raw progress toward this milestone (server amount, capped at target). */
    amount: number;
    /** Milestone target amount. */
    target: number;
    /** Percent complete (0–100) — drives the per-step progress line so partial (e.g. sub-hour) progress is visible. */
    pct: number;
    /**
     * Admin-authored action for this task (QuestConfigMilestone.url) — same
     * mechanism the challenge/game pages render. Internal path → navigate,
     * external → new tab. Undefined = plain progress row.
     */
    url?: string;
    /**
     * Admin-authored milestone description (QuestConfigMilestone.description) —
     * surfaced as a hover/focus tooltip so per-milestone instructions/qualifiers
     * (e.g. "Refer a Friend — must be a different household and the referral must
     * complete the onboarding quest to qualify") show without cluttering the row.
     */
    description?: string;
    /**
     * react-icons key authored on the milestone (QuestConfigMilestone.icon) —
     * the journey map draws a per-stop icon rather than an index
     * (`play-the-run.html`). Unset falls back to the generic goal icon.
     */
    icon?: string;
    /**
     * Short reward label for this stop ("+100 XP", "$1 Credit", "LootBox"),
     * formatted from the milestone's populated reward configs. Undefined when
     * the milestone pays nothing — the anchor renders no chip in that case.
     */
    reward?: string;
}

/** One renderable Play-page quest card, fully derived from server data. */
export interface PlayPageQuestCard {
    quest: QuestConfigResponse;
    progression?: QuestProgression;
    steps: DerivedStep[];
    /** True when a QUEST_COMPLETION prerequisite isn't complete yet. */
    locked: boolean;
    /** Name of the quest whose completion unlocks this one (for the lock message). */
    unlockedBy?: string;
    complete: boolean;
    claimed: boolean;
    /**
     * Completion-window fields (see quest-completion-window feature). All
     * undefined for a quest with no window — the card behaves exactly as before.
     */
    /** This quest's configured per-user window, in days (for the locked "N days to complete" hint). */
    completionWindowDays?: number;
    /** This user's absolute deadline (epoch ms), stamped once the quest is unlocked. */
    deadlineAt?: number;
    /**
     * Journey context — where this segment sits on the authored track, for the
     * card's position dots (`play-the-run-card.html`, seg.pos). This CANNOT be
     * derived from the surfaced quests: one-at-a-time sequencing drops every
     * resolved segment server-side, so the client only ever sees the live one.
     * Undefined when the quest belongs to no journey — the card then falls back
     * to its position among the visible cards, exactly as before.
     */
    journeyPosition?: number;
    /** Total segments on that track (the dot count). */
    journeyTotal?: number;
    /**
     * The segment-completion reward shown in the masthead badge ("Common
     * LootBox"). First quest-level reward config; undefined when the segment
     * has no quest-level reward, in which case the badge doesn't render.
     */
    segmentReward?: RewardConfigResponse;
}

/**
 * Short reward label for a journey stop — "+100 XP", "$1 Credit", "x3 Moves"
 * (`play-the-run.html` per-stop `rw` captions). Falls back to the reward's
 * authored name so an unmapped type still reads as something real rather than
 * a fabricated amount.
 */
function rewardLabel(reward: RewardConfigResponse | undefined): string | undefined {
    if (!reward) {
        return undefined;
    }
    const amount = reward.amount;
    switch (reward.type) {
        case RewardType.XP:
            return amount ? `+${amount} XP` : reward.name;
        case RewardType.FUEL:
            return amount ? `$${amount} Fuel` : reward.name;
        case RewardType.LOOTSHOP_CREDIT:
            return amount ? `$${amount} Credit` : reward.name;
        case RewardType.FUELUP_MOVES:
            return amount ? `x${amount} Moves` : reward.name;
        default:
            return reward.name;
    }
}

function questPrerequisites(quest: QuestConfigResponse): Prerequisite[] {
    return Array.isArray(quest.prerequisites) ? quest.prerequisites : [];
}

function questCompletionPrereqIds(quest: QuestConfigResponse): string[] {
    return questPrerequisites(quest)
        .filter(p => p.prerequisiteType === PrerequisiteType.QUEST_COMPLETION && p.questId)
        .map(p => p.questId as string);
}

/** Ordered milestones off a quest config. */
export function questMilestones(quest: QuestConfigResponse | undefined): QuestConfigMilestoneResponse[] {
    return Array.isArray(quest?.milestones) ? quest.milestones : [];
}

/**
 * Derive task rows from the quest's milestones + server progression. Counters
 * use the milestone's own targetAmount (and displayDivider when configured,
 * e.g. seconds→hours), so "0h / 2h" comes from config, not copy.
 */
/*
 * Exported 2026-08-28 for the Drop surfaces' journey widgets (Phase H rows
 * 8-9): The Road column derives its ladder through THIS function so a quest
 * renders identically on the play page and on a hub — one derivation, two
 * mounts, no copy to drift.
 */
export function deriveSteps(quest: QuestConfigResponse, progression: QuestProgression | undefined): DerivedStep[] {
    const milestones = questMilestones(quest);
    let currentAssigned = false;
    const now = Date.now();
    return milestones.map((m) => {
        const milestoneId = m.id;
        const entry = (progression?.progression as Record<string, { amount?: number; isComplete?: boolean }> | undefined)?.[milestoneId];
        const done = Boolean(entry?.isComplete);
        /*
         * A step that has not opened is not the current one — "what do I do
         * now" has to name something a member can actually do today, and a
         * ladder that introduces one task a week would otherwise point at
         * next Monday's.
         *
         * It is still evaluated for `done`: a member who completed the
         * underlying action early keeps the credit when the step opens.
         */
        const locked = typeof m.opensAt === "number" && m.opensAt > now;
        const current = !done && !locked && !currentAssigned;
        if (current) currentAssigned = true;

        const target = Number(m.targetAmount ?? 0);
        const rawAmount = Number(entry?.amount ?? 0);
        const amount = target > 0 ? Math.min(rawAmount, target) : rawAmount;
        const pct = target > 0 ? Math.min(100, Math.round(amount / target * 100)) : done ? 100 : 0;

        let progressText: string | undefined;
        if (target > 1) {
            const divider = Number(m.displayDivider ?? 1) || 1;
            if (divider > 1) {
                /*
                 * Time-based: displayDivider converts the raw unit → hours. A
                 * fractional hour ("0.1h") is hard to read, so show whole minutes
                 * while under an hour ("6m / 2h") and hours once past it
                 * ("1h 12m / 2h"). Minutes = fraction-of-hour × 60, so this works
                 * whether the base unit is seconds (divider 3600) or minutes (60).
                 */
                const targetHours = Math.floor(target / divider);
                const hours = amount / divider;
                if (hours < 1) {
                    progressText = `${Math.floor(hours * 60)}m / ${targetHours}h`;
                } else {
                    const wholeHours = Math.floor(hours);
                    const mins = Math.round((hours - wholeHours) * 60);
                    progressText = mins > 0
                        ? `${wholeHours}h ${mins}m / ${targetHours}h`
                        : `${wholeHours}h / ${targetHours}h`;
                }
            } else {
                progressText = `${amount} / ${target}`;
            }
        } else if (target === 1) {
            // Single-shot tasks (e.g. "Share a Clip") still get a count so the row isn't blank.
            progressText = `${done ? 1 : 0} / 1`;
        }

        return {
            label: m.name ?? "",
            done,
            current,
            locked,
            opensAt: locked ? m.opensAt : undefined,
            progressText,
            url: m.url,
            amount,
            target,
            pct,
            description: m.description,
            icon: m.icon,
            reward: rewardLabel(m.rewardConfigs?.[0]),
        };
    });
}

/** Prerequisite depth for ordering: no-prereq quests first, then their dependents. */
function prereqDepth(quest: QuestConfigResponse, byId: Map<string, QuestConfigResponse>, seen: Set<string> = new Set()): number {
    const ids = questCompletionPrereqIds(quest);
    if (!ids.length) return 0;
    const questId = quest.id ?? "";
    if (seen.has(questId)) return 0; // cycle guard
    seen.add(questId);
    return 1 + Math.max(...ids.map(id => {
        const parent = byId.get(id);
        return parent ? prereqDepth(parent, byId, seen) : 0;
    }));
}

export function usePlayPageQuests() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();
    const [claimingQuestId, setClaimingQuestId] = useState<string | undefined>();

    const { data: playQuests } = useGetQuestConfigsForUser(fullToken, { surface: QuestDisplay.PLAY_PAGE });

    /*
     * The authored journeys for this surface — the track each surfaced segment
     * sits on. Only used for position/total; unlocking still runs entirely off
     * the segment quests' prerequisites, so a missing/empty journey degrades to
     * the pre-journey behaviour rather than hiding anything.
     */
    const { data: journeys } = useGetJourneysForUser(fullToken, { surface: QuestDisplay.PLAY_PAGE });

    const journeyByQuestId = useMemo(() => {
        const map = new Map<string, { position: number; total: number }>();
        for (const journey of journeys ?? []) {
            const segments = journey.segments ?? [];
            /*
             * Position comes from the server-derived `order`, NOT the array
             * index. The backend walks the prerequisite graph to work out how
             * far along the track each segment sits, and branch alternates
             * deliberately SHARE an order because they unlock in parallel.
             * Reading the index instead counted each alternate as its own stop,
             * so a branching track — the whole point of the run — reported
             * "segment 4 of 6" for a four-stop path.
             */
            const total = new Set(segments.map(segment => segment.order)).size;
            for (const segment of segments) {
                /*
                 * First journey to claim a quest wins. Until the track router
                 * lands, every live track is returned for this surface and a
                 * quest can belong to more than one — overwriting made the
                 * player's position depend on the order the server happened to
                 * return journeys in.
                 */
                if (!segment.questConfigId || map.has(segment.questConfigId)) {
                    continue;
                }
                map.set(segment.questConfigId, { position: segment.order, total });
            }
        }
        return map;
    }, [journeys]);

    const quests = useMemo(() => {
        const list = playQuests ?? [];
        const byId = new Map(list.map(q => [q.id ?? "", q]));
        return [...list].sort((a, b) => prereqDepth(a, byId) - prereqDepth(b, byId));
    }, [playQuests]);

    const questIds = useMemo(
        () => quests.map(q => q.id).filter((id): id is string => Boolean(id)),
        [quests],
    );
    const { data: progressions } = useGetQuestProgressionForUser(fullToken, questIds);

    /*
     * `ready` gates card rendering so consumers never show a quest card built
     * from unresolved progression. The progression query is `enabled` only
     * once questIds exist (it depends on the configs query), so configs always
     * resolve first — and while `progressions` is still undefined every card
     * computes claimed=false, which flash-renders the Welcome card for a user
     * who already completed it, then corrects to the next card. Ready =
     * configs settled AND (no quests, or progressions settled).
     */
    const ready = !fullToken
        /*
         * Unauthenticated: the progression query is auth-gated and never fires,
         * so it can never settle — resolve immediately (no quest cards render
         * for a signed-out user anyway) rather than hang on the skeleton.
         */
        ? true
        : playQuests !== undefined && (questIds.length === 0 || progressions !== undefined);

    const cards: PlayPageQuestCard[] = useMemo(() => {
        const byId = new Map(quests.map(q => [q.id ?? "", q]));
        const progressionFor = (questId: string | undefined) =>
            progressions?.find(p => p.questConfigId === questId);

        return quests.map(quest => {
            const questId = quest.id;
            const progression = progressionFor(questId);
            const prereqIds = questCompletionPrereqIds(quest);
            const unmet = prereqIds.filter(id => !progressionFor(id)?.isComplete);
            const unlockedBy = unmet.length
                ? byId.get(unmet[0])?.name
                : undefined;
            const complete = Boolean(progression?.isComplete);
            /*
             * A quest-level claim (POST /quest-configs/:id/claim-reward with the quest's
             * own id) stamps the TOP-LEVEL questProgression.claimedAt first, before any
             * reward work — and for reward-less / auto-reward quests it returns early
             * without ever stamping per-milestone claimedAt. Reading only milestone
             * claimedAt therefore left a claimed social/onboarding quest computing
             * claimed=false, so its CLAIM REWARD button stayed and every re-click 400'd
             * "Reward has already been claimed." Treat the top-level claimedAt as
             * authoritative and fall back to the milestone stamps.
             */
            const claimed = complete && (
                Boolean(progression?.claimedAt) ||
                Object.values((progression?.progression as Record<string, { claimedAt?: number }> | undefined) ?? {})
                    .some(m => Boolean(m?.claimedAt))
            );
            const deadlineAt = progression?.deadlineAt;
            const journey = questId ? journeyByQuestId.get(questId) : undefined;
            return {
                quest,
                progression,
                steps: deriveSteps(quest, progression),
                locked: unmet.length > 0,
                unlockedBy,
                complete,
                claimed,
                completionWindowDays: quest.completionWindowDays,
                deadlineAt,
                journeyPosition: journey?.position,
                journeyTotal: journey?.total,
                segmentReward: quest.rewardConfigs?.[0],
            };
        });
    }, [quests, progressions, journeyByQuestId]);

    /*
     * Visible cards = the list the mount points render — the ACTIVE SEGMENT,
     * and nothing else.
     *
     * The journey is a single card the player advances through, not a carousel
     * of segments. Only one segment is ever on screen: the one they are on.
     * The next is revealed when the current one resolves, which is the whole
     * point of a journey — showing what's ahead spoils the steps and rewards
     * it exists to unfold, and offers cards that can't be acted on.
     *
     * A segment is eligible to BE the active one unless:
     *  - it belongs to no authored Journey, OR
     *  - it is LOCKED, i.e. the user has not reached it yet, OR
     *  - it has been CLAIMED. Claiming is what advances the journey: the
     *    reward is confirmed by a toast and the next segment takes the card
     *    immediately. Holding the finished segment on screen behind a Dismiss
     *    button made the player do bookkeeping to see what they had unlocked.
     *
     * `cards` is ordered by prerequisite depth, so the first eligible entry is
     * the earliest unresolved segment — the active one.
     */
    const visibleCards = useMemo(
        () => cards.filter(c => {
            /*
             * ONLY journey segments surface here. The Play rail is the journey
             * — a PLAY_PAGE quest that belongs to no authored Journey is not
             * part of the run and must not appear as a card or as a Next Move,
             * and when no journey is authored at all, no quest shows. Filtering
             * at this single source (rather than in the carousel and again in
             * Next Moves) keeps the Next Moves rows index-aligned with the
             * carousel slides they link to.
             */
            if (c.journeyPosition == null) return false;
            /*
             * A segment the user has NOT reached yet does not surface at all.
             * The journey's whole premise is that the next stop is revealed by
             * completing the current one — rendering a locked segment as its
             * own slide spoils every step and reward ahead of the player, and
             * hands them a card they cannot act on.
             *
             * `locked` is true while any QUEST_COMPLETION prerequisite is unmet
             * (see `unmet` above), so the first segment — which has no
             * prerequisites — is always reachable and the rail is never empty.
             * A segment appears the moment its predecessor is completed.
             */
            if (c.locked) return false;
            return !c.claimed;
        /*
         * One card — the active segment. `cards` is prerequisite-ordered, so
         * the first survivor is the earliest unresolved stop.
         */
        }).slice(0, 1),
        [cards],
    );

    /*
     * Claim any completed quest's reward → invalidate quest + progression
     * caches (dependents' prerequisites clear on refetch).
     *
     * The claim IS the advance: a toast confirms the reward and the finished
     * segment drops out, so the next one takes the card on the same refetch
     * that clears its prerequisite. Callers pass onClaimed for mount-point
     * follow-up (e.g. resetting the carousel index).
     */
    const claimMutation = useClaimQuestReward(fullToken ?? undefined);
    const claim = (questId: string | undefined, onClaimed?: () => void) => {
        if (!questId) return;
        setClaimingQuestId(questId);
        claimMutation.mutate(questId, {
            onSuccess: async () => {
                toast.success("Reward claimed");
                /*
                 * AWAIT the refetch. `onSettled` clears claimingQuestId, which
                 * is what disables the Claim button — and `claimed` is derived
                 * from the progression query, which serves the PRE-claim value
                 * throughout the refetch (it uses `placeholderData:
                 * keepPreviousData`). Firing these without awaiting re-enables
                 * "Claim Reward" on a quest that was just claimed, and a second
                 * click on a slow connection double-submits against an
                 * already-claimed quest — the exact loop the onError branch
                 * below exists to recover from. Holding the mutation open until
                 * fresh progression lands keeps the button disabled right up to
                 * the moment the segment drops out of visibleCards.
                 */
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ["quest-configs-user"] }),
                    queryClient.invalidateQueries({ queryKey: ["quest-progression-user"] }),
                ]);
                onClaimed?.();
            },
            /*
             * Reconcile with the server on failure. The dominant failure is a
             * claim that ALREADY succeeded server-side (reward distributed,
             * claimedAt stamped) but whose success the client missed — every
             * repeat click then 400s "Reward has already been claimed." Without
             * this, the stale card keeps rendering the claim button and the user
             * loops on it forever. Refetching pulls the true progression
             * (carrying claimedAt) so the card computes claimed=true and drops
             * out of the carousel; a genuinely unclaimed quest refetches as
             * unclaimed and correctly keeps its button.
             */
            onError: () => {
                queryClient.invalidateQueries({ queryKey: ["quest-configs-user"] });
                queryClient.invalidateQueries({ queryKey: ["quest-progression-user"] });
            },
            onSettled: () => setClaimingQuestId(undefined),
        });
    };

    /*
     * Play streak — first streak the user has; currentStreak lives on the nested
     * `streak` (UserStreakResponse), name on `config` (StreakConfigResponse). The
     * previous cast read `streakConfig`, which is not a field on MyStreakEntry — so
     * streakName was silently always undefined. Using the generated type surfaces the
     * correct field name.
     */
    const { data: streaks } = useMyStreaks(fullToken);
    const streakEntry = streaks?.streaks?.[0];

    return {
        cards,
        visibleCards,
        ready,
        claimingQuestId,
        claim,
        streakCurrent: streakEntry?.streak?.currentStreak,
        streakName: streakEntry?.config?.name,
    };
}
